import * as React from 'react'
import { classnames } from '@halo/_utils/classnames'
import { SUPPORTED_BILLING_CURRENCIES } from '@halo/constants'
import { numberAsCurrency, numberWithCommas } from '@halo/_utils/numeral'
import * as moment from 'moment-timezone'
import './account-charge-details.scss'
import { AccountService } from '@halo/services/account.service'
import { CompoundTable } from '@halo/components/compound-table/compound-table'
import { Loading3QuartersOutlined, WarningFilled } from '@ant-design/icons'
import { Table, Tooltip } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { Drawer, Well } from '@pushly/aqe/lib/components'
import { useService } from '@halo/hooks/use-service'
import { AccountBillingType } from '@halo/enums/account-billing-type'
import { AccountBillingRateType } from '@halo/enums/account-billing-rate-type'
import FormLayoutRow from '@halo/components/form-layout-row/form-layout-row'
import {
    BillingDataResponse,
    BillingRecordResponse,
    GetBillingRecordLogResponse,
} from '@pushly/models/lib/structs/billing'

interface IInvoiceTypeBillingData {
    type: AccountBillingType.INVOICE
    currency: string
}

interface ICreditTypeBillingData {
    type: AccountBillingType.CREDIT
    currency: string
    rate_type: AccountBillingRateType
    rate: number
}

interface IStripeCustomerData {
    stripe_customer_id?: string
    stripe_customer_email?: string
    stripe_customer_last4?: string
}

type BillingDataValues = (IInvoiceTypeBillingData | ICreditTypeBillingData) & IStripeCustomerData

interface IBillingRecordWithLogs {
    record: BillingRecordResponse
    logs: GetBillingRecordLogResponse[]
    loadingLogs: boolean
}

// TODO: should we export/extract for test?
const attachBillingRecordLogs = (
    record: BillingRecordResponse,
    logs: GetBillingRecordLogResponse[],
): IBillingRecordWithLogs => {
    const sortedLogs = [...logs].sort((a: GetBillingRecordLogResponse, b: GetBillingRecordLogResponse) => {
        const ac = moment(a.event_time_utc)
        const bc = moment(b.event_time_utc)
        return ac < bc ? 1 : ac > bc ? -1 : 0
    })

    return {
        record,
        logs: sortedLogs,
        loadingLogs: false,
    }
}

// TODO: should we export/extract for test?
const getBillingDataValues = (billingData: Partial<BillingDataResponse>): BillingDataValues => {
    const type = AccountBillingType.fromString(billingData.type ?? 'invoice')
    const currency: string = (billingData.currency || 'USD').toUpperCase()

    let values: BillingDataValues
    switch (type) {
        case AccountBillingType.CREDIT:
            values = {
                type,
                currency,
                rate_type: AccountBillingRateType.fromString(billingData.rate_type ?? 'FLAT_RATE'),
                rate: billingData.rate ?? 0,
            }
            break
        case AccountBillingType.INVOICE:
        default:
            values = {
                type,
                currency,
            }
            break
    }

    if (billingData.stripe_customer_id !== undefined) {
        values.stripe_customer_id = billingData.stripe_customer_id.toString()
        values.stripe_customer_email = billingData.stripe_customer_email?.toString()
        values.stripe_customer_last4 = billingData.stripe_customer_last4?.toString()
    }

    return values
}

interface IAccountChargeDetails {
    account: any
    record: BillingRecordResponse | false
    visible: boolean
    onClose: (e: any) => void
}

export const AccountChargeDetails: React.FC<IAccountChargeDetails> = (props) => {
    const accountService = useService(AccountService)

    const [state, setState] = React.useState<IBillingRecordWithLogs | null>(
        props.record ? { record: props.record, logs: [], loadingLogs: true } : null,
    )

    const record = state?.record
    const recordMeta = record?.metadata ?? {}
    const propsValue = props.account.billing_data ?? {}

    const currentValues = getBillingDataValues(propsValue)
    const stripeCustomerLast4 = recordMeta.stripe_customer_last4 || currentValues.stripe_customer_last4

    const currentCurrencyDisplayName: string = SUPPORTED_BILLING_CURRENCIES.find(
        (cur) => cur.value === currentValues.currency,
    )!.label

    const currentCurrencySymbol: string = SUPPORTED_BILLING_CURRENCIES.find(
        (cur) => cur.value === currentValues.currency,
    )!.symbol

    React.useEffect(() => {
        if (props.account && record) {
            const fetchLogs = async () => {
                const { ok, data: logs } = await accountService.fetchBillingRecordLogs(
                    props.account.id,
                    record.id,
                    { pagination: 0 },
                    false,
                    `recordLogs-${record.id}`,
                )

                if (ok) {
                    setState(attachBillingRecordLogs(record, logs))
                }
            }

            setState({ record, logs: [], loadingLogs: true })
            fetchLogs()
        }
    }, [props.account?.id, props.record && props.record.id])

    return (
        <Drawer
            className={classnames('acd')}
            title={`Payment Record ${
                props.visible && record && `- ${props.account.name} | ${recordMeta.billable_period}`
            }`}
            placement="right"
            closable={true}
            hideFooter={!record || !record.stripe_charge_receipt_url}
            submitText="Get Receipt"
            onSubmit={() => record && window.open(record.stripe_charge_receipt_url)}
            onClose={() => props.onClose(null)}
            open={props.visible}
        >
            {props.visible && record && (
                <>
                    <Well title="Details" hideFooter={true}>
                        <FormLayoutRow label="Billing Date" value={moment(record.created_at).format('LLL')} />

                        <FormLayoutRow label="Billable Period" value={recordMeta.billable_period} />

                        <FormLayoutRow
                            label="Billable Subs"
                            value={numberWithCommas(recordMeta.billable_subscribers)}
                        />

                        <FormLayoutRow label="Currency" value={currentCurrencyDisplayName} />

                        <FormLayoutRow label="Description" value={recordMeta.description} />

                        <FormLayoutRow
                            label="Amount"
                            value={
                                <>
                                    {currentCurrencySymbol}
                                    {numberAsCurrency(recordMeta.amount)}
                                </>
                            }
                        />

                        <FormLayoutRow label="Notes" value={recordMeta.notes || 'None'} />
                    </Well>

                    <Well title="Stripe Configuration" hideFooter={true}>
                        <FormLayoutRow label="Customer Reference ID" value={currentValues.stripe_customer_id} />

                        <FormLayoutRow label="Invoice Reference ID" value={record.stripe_invoice_id} />

                        <FormLayoutRow label="Charge Reference ID" value={record.stripe_charge_id} />

                        <FormLayoutRow
                            label="Credit Card Used"
                            value={
                                !currentValues.stripe_customer_id ? <>N/A</> : <>**** **** **** {stripeCustomerLast4}</>
                            }
                        />
                    </Well>

                    {record.metadata.stripe_error?.message && (
                        <Well
                            title={
                                <>
                                    <WarningFilled style={{ color: '#f04134' }} />
                                    &nbsp;
                                    <span>Failure Reason</span>
                                </>
                            }
                            hideFooter={true}
                        >
                            {record.metadata.stripe_error?.message}
                        </Well>
                    )}

                    <Well title="Event Logs" hideFooter={true}>
                        <CompoundTable>
                            <Table
                                size="small"
                                className={classnames('acd-event-logs')}
                                dataSource={state?.logs ?? []}
                                loading={
                                    !!state?.loadingLogs && {
                                        indicator: <Loading3QuartersOutlined spin={true} />,
                                    }
                                }
                                locale={{
                                    emptyText: <AntdTableEmptyPlaceholder text="No Events" />,
                                }}
                                pagination={false}
                                showHeader={false}
                            >
                                <Table.Column
                                    className="log-id"
                                    title="ID"
                                    key="id"
                                    dataIndex="id"
                                    render={(value, log: GetBillingRecordLogResponse) => {
                                        // Show "Remote Event" with tooltip for Stripe-sourced logs
                                        if (log.source === 'stripe') {
                                            return (
                                                <Tooltip title={value}>
                                                    <span>Remote Event</span>
                                                </Tooltip>
                                            )
                                        }
                                        return value
                                    }}
                                />
                                <Table.Column
                                    className="log-action"
                                    title="Action"
                                    key="action"
                                    dataIndex="event_meta"
                                    render={(value, _log: GetBillingRecordLogResponse) => {
                                        let text = 'Unknown'
                                        const stripeChargeStatus = record.stripe_charge_status

                                        switch (value?.action) {
                                            case 'ACCOUNT_PAYMENT_CREATE':
                                                text = 'Payment Created'
                                                break
                                            case 'ACCOUNT_PAYMENT_CONFIRM':
                                                text = 'Payment Confirmed'
                                                if (stripeChargeStatus === 'FAILED') {
                                                    text = 'Payment Failed'
                                                }
                                                break
                                            case 'STRIPE_CHARGE_SUCCEEDED':
                                                text = 'Charge Succeeded'
                                                break
                                            case 'STRIPE_CHARGE_FAILED':
                                                text = 'Charge Failed'
                                                break
                                            case 'STRIPE_CHARGE_PENDING':
                                                text = 'Charge Pending'
                                                break
                                            case 'STRIPE_CHARGE_UPDATED':
                                                text = 'Charge Updated'
                                                break
                                            case 'STRIPE_CHARGE_REFUNDED':
                                                text = 'Charge Refunded'
                                                break
                                        }

                                        return text
                                    }}
                                />
                                <Table.Column
                                    className="log-time"
                                    title="Event Time"
                                    key="event_time_utc"
                                    dataIndex="event_time_utc"
                                    render={(value, _log: GetBillingRecordLogResponse) => {
                                        return moment.tz(value, 'America/Chicago').format('LLLL')
                                    }}
                                />
                            </Table>
                        </CompoundTable>
                    </Well>
                </>
            )}
        </Drawer>
    )
}
